import React, {memo, useEffect} from 'react'
import {Button, Dropdown, Form, Header, List, Message, Modal} from 'semantic-ui-react'
import _ from 'lodash'
import {
  withEnvironmentData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withResourceData,
  withServiceData,
  withVersionData
} from '../../../../../hoc'
import {isNumber, asOptions, goBack} from '../../../../../util'
import {upsertResource} from '../../../../../backend'
import {useSafeState} from '../../../../../hook'

const _ResourceUpsert = (
  {
    service,
    version,
    environment,
    resource,
    history,
    location,
    navigate,
    mutateEnvironment,
    mutateResource,
    upsertResource,
    basePath = '/c/services/:serviceId/versions/:versionId/environments/:environmentId/resources'
  }
) => {

  const [state, setState] = useSafeState({
    verb: '',
    path: '',
    weight: 0,
    errors: {},
    serviceId: service.id,
    versionId: version.id,
    environmentId: environment.id,
    ...(resource || {}),
    resource: (resource || {})
  })

  useEffect(() => {
    setState({
      ...state,
      serviceId: service.id,
      versionId: version.id,
      environmentId: environment.id,
      ...(resource || {}),
      resource: (resource || {})
    })
  }, [service, version, environment, resource])

  const getPathPrefix = () => {
    return basePath
      .replace(':serviceId', service.id)
      .replace(':versionId', version.id)
      .replace(':environmentId', environment.id)
  }

  const validate = ({path, verb, weight, resource = {}, errors = {}}) => {
    if (!verb) {
      errors['resource.verb.required'] = 'Verb required'
    } else {
      delete errors['resource.verb.required']
    }
    if (!path) {
      errors['resource.path.required'] = 'Path required'
    } else {
      delete errors['resource.path.required']
      if (path.length > 2048) {
        errors['resource.path.exceeds.max.length'] = 'Path cannot exceed 2048 characters'
      } else {
        delete errors['resource.path.exceeds.max.length']
      }
    }
    if ((resource.path !== path && resource.verb !== verb && isResourcePresent(environment.resources, path, verb))) {
      errors['resource.taken'] = 'Path/Verb combo already present. Change path or verb and try again'
    } else {
      delete errors['resource.taken']
    }
    if (weight === null || weight === undefined || weight === '') {
      errors['resource.weight.required'] = 'Weight required'
    } else {
      delete errors['resource.weight.required']
      if (!isNumber(weight)) {
        errors['resource.weight.numeric'] = 'Weight must be numeric'
      } else {
        delete errors['resource.weight.numeric']
      }
    }
    return errors
  }


  const onInputChange = (e) => {
    let updatedState = {...state, ...{[e.target.name]: e.target.value}}
    let errors = {}
    if (state.submitted) {
      errors = validate(updatedState)
      updatedState = {...updatedState, ...{errors}}
    }
    setState(updatedState)
  }

  const onClose = () => {
    goBack(history, navigate, getPathPrefix())
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)
    if (_.keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    setState({...state, ...{submitting: true}})
    const resource = await upsertResource(_.omit(state, ['responses']))
    setState({...state, ...{submitting: false}})

    mutateEnvironment()
    mutateResource()

    if (!state.id) {
      navigate(`${getPathPrefix()}/${resource.id}/responses`)
    } else {
      goBack(history, navigate, getPathPrefix())
    }
  }

  const {path = '', verb = '', weight = 0, errors, submitting} = state
  const editing = !!state.resource.id
  const hasResourceTakenError = Object.keys(errors).some((k) => k.indexOf('resource.taken') > -1)
  const hasVerbError = Object.keys(errors).some((k) => k.indexOf('resource.verb') > -1)
  const hasPathError = Object.keys(errors).some((k) => k.indexOf('resource.path') > -1)
  const hasWeightError = Object.keys(errors).some((k) => k.indexOf('resource.weight') > -1)
  const hasErrors = hasResourceTakenError || hasVerbError || hasPathError || hasWeightError
  const verbs = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD']

  if (!state.resource.path && location.pathname.endsWith('edit')) {
    return null
  }

  return (
    <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={onClose} data-testid='resource-upsert-modal'>
      <Header content={`${editing ? 'Edit Resource' : 'Add Resource'}`}/>
      <Modal.Content>
        <Form error={hasErrors}>
          <Form.Field error={hasResourceTakenError || hasVerbError} required>
            <label>Verb</label>
            <Dropdown options={asOptions(verbs)} selection search item
                      value={verb}
                      placeholder='Select status code'
                      data-testid='resource-verb-dropdown'
                      onChange={(e, {value}) => onInputChange({
                        target: {
                          name: 'verb',
                          value
                        }
                      })}/>
          </Form.Field>
          <Form.Field error={hasResourceTakenError || hasPathError} required>
            <label>Path</label>
            <Form.Input placeholder='Enter resource path'
                        name='path'
                        value={path}
                        data-testid='resource-path-textbox'
                        onChange={onInputChange}/>
          </Form.Field>
          <Form.Field error={hasWeightError} required>
            <label>Weight</label>
            <Form.Input placeholder='Enter weight' name='weight' value={weight} onChange={onInputChange}
                        data-testid='resource-weight-textbox'/>
          </Form.Field>
          <Message error data-testid='resource-upsert-errors'>
            <Message.Content>
              <List bulleted>
                {
                  Object.keys(errors).map((key) => (
                    <List.Item key={key}>{errors[key]}</List.Item>
                  ))
                }
              </List>
            </Message.Content>
          </Message>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} data-testid='resource-upsert-cancel-button'>Close</Button>
        <Button primary onClick={onSubmit}
                data-testid='resource-upsert-submit-button'
                loading={submitting}>{`${editing ? 'Update' : 'Add'}`}</Button>
      </Modal.Actions>
    </Modal>
  )
}

const isResourcePresent = (existing, path, verb) => {
  return existing.some((r) => r.path.toLowerCase() === path.toLowerCase() && r.verb.toLowerCase() === verb.toLowerCase())
}

let UpsertResource = (props) => {
  let {handleError} = props
  props = {
    ...props,
    history: window.history,
    upsertResource: handleError(upsertResource)
  }
  return (<_ResourceUpsert {...props}/>)
}

UpsertResource = withPropsPresent(UpsertResource, ['service', 'version', 'environment', (props) => !props.resourceId || !!props.resource])
UpsertResource = withHelmet(UpsertResource, (props) => {
  const {resource} = props
  if (resource && resource.id) {
    return 'Edit Resource'
  }
  return 'Add Resource'
})
UpsertResource = withResourceData(UpsertResource, 'Loading resource...')
UpsertResource = withEnvironmentData(UpsertResource, 'Loading environment...')
UpsertResource = withVersionData(UpsertResource, 'Loading version...')
UpsertResource = withServiceData(UpsertResource, 'Loading service...')
UpsertResource = withErrorHandling(UpsertResource)
UpsertResource = memo(UpsertResource)

export {_ResourceUpsert, UpsertResource}

export default UpsertResource